import { getScopes } from "@/api/apiclient/scopes";
import { ScopeEntityType, ScopePhaseType, ScopeType } from "@/types/scopes";
import { defineStore } from "pinia";
import { useDynamicOpportunityRoutes } from "@/composables/useDynamicOpportunityRoutes";
import { SidebarSubitemType } from "@/components/layout/business/Sidebar.vue";

export const useScope = defineStore("scope", {
  state: () => {
    return {
      scopeList: [] as Array<ScopeType>,
      tabs: [
        { id: "details", title: "Dettagli", disabled: false },
        { id: "actions", title: "Azioni", disabled: false },
        { id: "entities", title: "Entità", disabled: false },
      ] as Array<{ id: string; title: string; disabled?: boolean }>,
      opportunityRouteSubitems: [] as Array<SidebarSubitemType>,
      defaultTab: "details",
    };
  },
  actions: {
    async setup(force = false) {
      const { addDynamicOpportunityRoutes, buildOpportunitySidebarSubitems } =
        useDynamicOpportunityRoutes();
      if (this.scopeList.length && !force) return;
      this.scopeList.length = 0;
      const resScopes = await getScopes();
      this.scopeList.push(
        ...(resScopes?.data.scopes?.sort((a: ScopeType, b: ScopeType) =>
          a.name.localeCompare(b.name)
        ) ?? [])
      );
      // update opportunity/pipeline route subitems
      addDynamicOpportunityRoutes(this.scopeList);
      // update scopeStore opportunityRouteSubitems
      this.opportunityRouteSubitems = buildOpportunitySidebarSubitems(this.scopeList);
    },
    resetList(): void {
      this.scopeList.length = 0;
    },
  },
  getters: {
    getNameFromId(state) {
      return (id: number | null): string => {
        return state.scopeList.find((i) => i.id == id)?.name ?? "";
      };
    },
    getCodeFromId(state) {
      return (id: number | null): string => {
        return state.scopeList.find((i) => i.id == id)?.code ?? "";
      };
    },
    getScopeById(state) {
      return (id?: number): ScopeType | null => {
        return state.scopeList.find((i) => i.id == id) ?? null;
      };
    },
    getScopeEntitiesFromScopeId(state) {
      return (id: number | null): ScopeEntityType[] | undefined => {
        return state.scopeList.find((i) => i.id == id)?.scopeEntities;
      };
    },
    getScopeEntityByIdAndByScopeId(state) {
      return (scopeId?: number, entityId?: number): ScopeEntityType | null => {
        return (
          state.scopeList
            .find((i) => i.id == scopeId)
            ?.scopeEntities?.find((e) => e.id == entityId) ?? null
        );
      };
    },
    getScopeListStringified(state) {
      return state.scopeList.map((i) => i.id).join(",");
    },
    getOpportunitySubitem(state) {
      return (scopeId?: number): SidebarSubitemType | null => {
        const scope = state.scopeList.find((s) => s.id === scopeId);
        if (!scope) return null;
        return {
          text: scope.name,
          to: { name: `manageOpportunity-${scope.code}` },
        };
      };
    },
    getIdFromCode(state) {
      return (code?: string): number | null => {
        return state.scopeList.find((i) => i.code === code)?.id ?? null;
      };
    },
    getPhaseFromConfig(state) {
      return (scopeId: number, phaseId: number): ScopePhaseType | null => {
        const scope = state.scopeList.find((s) => s.id === scopeId);
        if (!scope) return null;

        const findPhase = (phases: ScopePhaseType[]): ScopePhaseType | null => {
          for (const phase of phases) {
            if (phase.id === phaseId) return phase;
            const found = findPhase(phase.children ?? []);
            if (found) return found;
          }
          return null;
        };

        for (const entity of scope.scopeEntities) {
          const found = findPhase(entity.scopePhases);
          if (found) return found;
        }
        return null;
      };
    },
    getScopeEntityFromPhase(state) {
      return (scopeId: number, phaseId: number): ScopeEntityType | null => {
        const scope = state.scopeList.find((s) => s.id === scopeId);
        if (!scope) return null;

        const findPhase = (phases: ScopePhaseType[]): ScopePhaseType | null => {
          for (const phase of phases) {
            if (phase.id === phaseId) return phase;
            const found = findPhase(phase.children ?? []);
            if (found) return found;
          }
          return null;
        };

        for (const entity of scope.scopeEntities) {
          const found = findPhase(entity.scopePhases);
          if (found) return entity;
        }
        return null;
      };
    },
  },
});
